/*
 * hp-payment-terms — two small corrections to Shopify's <shopify-payment-terms>.
 *
 * The component itself is Shopify's and stays Shopify's: the wording, the
 * instalment maths, the Shop Pay lockup and the info modal are theirs. This file
 * does exactly two things the theme cannot do in CSS alone.
 *
 * ── 1. COLLAPSE THE MARGIN WHEN THE FILTER EMITTED NOTHING ────────────────
 * `payment_terms` emits nothing at all when the SHOP is not set up for Shop Pay
 * Installments. The wrapper's margin would then leave a gap under the price with
 * no content in it — CLAUDE.md's "a zero-height block still costs its MARGIN".
 *
 * That case is detectable synchronously: no <shopify-payment-terms> element in
 * the wrapper. No observer, no timers.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO is test the rendered height. An earlier
 * version did, on the belief that a product under Shopify's $35 floor rendered
 * an empty box. It does not: it renders a DIFFERENT message ("Pay over time for
 * orders over $35.00 with Shop Pay"), which is real content that deserves the
 * margin. That belief came from measuring the element before it had painted, and
 * the height test it produced was actively wrong — it collapsed the margin on
 * every low-priced product. If this ever needs revisiting, measure after the
 * component has settled, not on first read.
 *
 * ── 2. LET IT USE OUR TYPEFACE ─────────────────────────────────────────────
 * Shopify already sets `:host(shopify-payment-terms) { font-family: inherit }`,
 * so the host takes ours — but the inner wrapper carries `.font-sans`, which
 * re-sets the family to GTStandard-M and wins. Outside CSS cannot reach inside a
 * shadow root, so the only honest route is to add a stylesheet TO that root.
 *
 * It is `open`, and `adoptedStyleSheets` is appendable, so this pushes one extra
 * sheet that hands the family back to `inherit`. Purely additive: if Shopify
 * renames the id the rule stops matching and the component simply keeps its own
 * font. Nothing breaks.
 *
 * FONT-SIZE IS NOT DONE HERE — it already inherits (verified: setting 12px on
 * the wrapper moved the rendered text from 15px to 12px), so it belongs in the
 * stylesheet with everything else.
 */
(function () {
  const WRAPPER = '[data-hp-payment-terms]';
  const TERMS = 'shopify-payment-terms';

  /* The component's own root id. Only used to out-specify `.font-sans`; if it
     ever changes, the rule below is inert rather than wrong. */
  const FONT_PATCH = '#shopify-installments, #shopify-installments * { font-family: inherit; }';

  function patchFont(root) {
    if (root.__hpFontPatched) return;
    if (typeof CSSStyleSheet !== 'function' || !('adoptedStyleSheets' in root)) return;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(FONT_PATCH);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      root.__hpFontPatched = true;
    } catch (e) {
      /* Constructable stylesheets unavailable — the component keeps its own
         font, which is a cosmetic loss and not a broken page. */
    }
  }

  function markEmpty(wrapper, isEmpty) {
    wrapper.dataset.hpTermsEmpty = isEmpty ? 'true' : 'false';
  }

  function enhance(wrapper, attempt) {
    const terms = wrapper.querySelector(TERMS);

    /* No element at all: the shop is not set up for instalments, so the filter
       emitted nothing. Collapse immediately — there is nothing to wait for. */
    if (!terms) {
      markEmpty(wrapper, true);
      return;
    }

    /* The shadow root only exists once the element upgrades, and that is not
       guaranteed by the time this runs. Bounded polling rather than rAF, which
       is frozen in the browser pane (CLAUDE.md) and would make this untestable. */
    if (!terms.shadowRoot) {
      if (attempt < 30) setTimeout(() => enhance(wrapper, attempt + 1), 100);
      else markEmpty(wrapper, true);
      return;
    }

    patchFont(terms.shadowRoot);

    /* The element exists, so the component will render one of its two messages.
       Either is content, so the margin stays. */
    markEmpty(wrapper, false);
  }

  function init(wrapper) {
    enhance(wrapper, 0);

    /* `data-hp-variant-swap` replaces this wrapper's innerHTML on every variant
       change, which throws away the observed element AND the patched shadow
       root. Watching childList re-runs the whole enhancement against whatever
       replaced it, without this file needing to know how the swap works. */
    if (wrapper.__hpTermsMutation) return;
    wrapper.__hpTermsMutation = new MutationObserver(() => enhance(wrapper, 0));
    wrapper.__hpTermsMutation.observe(wrapper, { childList: true, subtree: true });
  }

  function start() {
    document.querySelectorAll(WRAPPER).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
